import { useState } from 'react';
import { Link, Navigate } from 'react-router';
import { AppFooter } from '../components/AppFooter';
import { Alert, Loading } from '../components/ui';
import { useI18n } from '../i18n';
import { useMe } from '../session';
import { ConsoleHeader } from './ConsolePage';

// The cost comparator for sellers: what the association pays today next to the three plans, before taxes, with the
// published prices (docs/business/pricing.md). Volunteer hours are recorded, never turned into dollars: they are
// measured during the trial. Other costs disappear only when the board confirms it (a box to tick).

const PLANS = [
  { key: 'autonome', name: 'Autonome', who: 'Le conseil garde la main, avec les assistants de Syndiqia.', rate: 6, min: 49, setup: 490, waivable: false,
    incl: ['Tous les assistants : demandes, cotisations, banque, entretien, assemblées', 'Le conseil approuve', 'Soutien par courriel'] },
  { key: 'assistee', name: 'Assistée', who: 'Le travail est fait, sans visites sur place.', rate: 17, min: 350, setup: 990, waivable: true,
    incl: ['Tout Autonome', 'Gestionnaire Syndiqia à distance : téléphone, budget, fin d’année, préparation des assemblées'] },
  { key: 'complete', name: 'Complète', who: 'Pour quitter une firme à mandat complet.', rate: 25, min: 500, setup: 1490, waivable: true,
    incl: ['Tout Assistée', 'Visites, supervision des entrepreneurs, urgences 24/7'] },
] as const;

const money = (n: number) => `${Math.round(n).toLocaleString('fr-CA')} $`;
const amount = (v: string) => {
  const n = parseFloat(v.replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export function ComparatorPage() {
  const { t } = useI18n();
  const me = useMe();
  const [lotsText, setLots] = useState('32');
  const [mode, setMode] = useState<'firm' | 'diy'>('firm');
  const [rate, setRate] = useState('35');
  const [firmMin, setFirmMin] = useState('0');
  const [extras, setExtras] = useState('0');
  const [other, setOther] = useState('0');
  const [hours, setHours] = useState('20');
  const [twoYear, setTwoYear] = useState(true);
  const [founder, setFounder] = useState(false);
  const [replaced, setReplaced] = useState(false);

  if (me.isPending) return <Loading label={t.loading} />;
  if (me.isError) return <Alert>{t.common.error}</Alert>;
  if (!me.data.staff.includes('platform_admin') && !me.data.staff.includes('sales')) return <Navigate to="/" replace />;

  const lots = Math.max(1, Math.round(amount(lotsText)) || 1);
  const firmMonthly = mode === 'firm' ? Math.max(lots * amount(rate), amount(firmMin)) : 0;
  const extrasYear = mode === 'firm' ? amount(extras) : 0;
  const otherYear = amount(other) * 12;
  const today = firmMonthly * 12 + extrasYear + otherYear;
  const fit = mode === 'diy' ? 'autonome' : amount(rate) >= 30 ? 'complete' : 'assistee';
  const detail = [
    mode === 'firm' ? `firme ${money(firmMonthly)}/mois` : null,
    extrasYear ? `extras ${money(extrasYear)}/an` : null,
    otherYear ? `autres ${money(otherYear / 12)}/mois` : null,
  ].filter(Boolean);
  const h = Math.round(amount(hours));

  return (
    <div className="chooser console cmp">
      <ConsoleHeader me={me.data} />
      <p>
        <Link to="/ventes">← {t.salesTools.title}</Link>
      </p>
      <h1>Comparateur de coûts</h1>
      <p className="muted">Ce que l’association paie aujourd’hui, à côté des trois forfaits Syndiqia. Montants avant taxes, avec les prix publiés. Remplissez avec les chiffres réels du conseil.</p>

      <div className="cmp-grid">
        <form className="panel cmp-inputs" onSubmit={(e) => e.preventDefault()}>
          <h2>La situation aujourd’hui</h2>
          <label className="cmp-field">
            <span>Nombre de lots ou d’unités</span>
            <input type="number" min={1} value={lotsText} onChange={(e) => setLots(e.target.value)} inputMode="numeric" />
          </label>
          <div className="cmp-field">
            <span>Qui gère?</span>
            <div className="cmp-seg">
              <button type="button" aria-pressed={mode === 'firm'} onClick={() => setMode('firm')}>Une firme</button>
              <button type="button" aria-pressed={mode === 'diy'} onClick={() => setMode('diy')}>Le conseil, bénévolement</button>
            </div>
          </div>
          {mode === 'firm' && (
            <>
              <label className="cmp-field">
                <span>Honoraires de la firme, par lot par mois</span>
                <input type="number" min={0} step={0.5} value={rate} onChange={(e) => setRate(e.target.value)} inputMode="decimal" />
                <small>Repère du marché : 25 à 27 $ pour un forfait léger, 33 à 37 $ pour un mandat complet.</small>
              </label>
              <label className="cmp-field">
                <span>Minimum mensuel de la firme</span>
                <input type="number" min={0} step={10} value={firmMin} onChange={(e) => setFirmMin(e.target.value)} inputMode="decimal" />
                <small>Souvent 210 à 350 $; 0 s’il n’y en a pas.</small>
              </label>
              <label className="cmp-field">
                <span>Frais facturés en plus, par année</span>
                <input type="number" min={0} step={50} value={extras} onChange={(e) => setExtras(e.target.value)} inputMode="decimal" />
                <small>Lettres de rappel, mises en demeure, assemblées supplémentaires, suivis de travaux…</small>
              </label>
            </>
          )}
          <label className="cmp-field">
            <span>Autres coûts d’administration, par mois</span>
            <input type="number" min={0} step={10} value={other} onChange={(e) => setOther(e.target.value)} inputMode="decimal" />
            <small>Tenue de livres, logiciel, envois postaux. Pas la vérification annuelle (inchangée).</small>
          </label>
          <label className="cmp-field">
            <span>Heures des bénévoles, par mois (estimation)</span>
            <input type="number" min={0} step={1} value={hours} onChange={(e) => setHours(e.target.value)} inputMode="numeric" />
            <small>Pour mémoire : on les mesure pendant l’essai, on ne les convertit pas en dollars.</small>
          </label>
          <label className="cmp-check">
            <input type="checkbox" checked={twoYear} onChange={(e) => setTwoYear(e.target.checked)} />
            <span><strong>Contrat de 2 ans</strong> Frais de mise en service annulés pour Assistée et Complète.</span>
          </label>
          <label className="cmp-check">
            <input type="checkbox" checked={replaced} onChange={(e) => setReplaced(e.target.checked)} />
            <span><strong>Les autres coûts disparaissent avec Syndiqia</strong> Seulement si le conseil le confirme; sinon ils restent ajoutés à chaque forfait.</span>
          </label>
          <label className="cmp-check">
            <input type="checkbox" checked={founder} onChange={(e) => setFounder(e.target.checked)} />
            <span><strong>Client fondateur</strong> Trois mois gratuits et prix garanti trois ans (premières associations seulement).</span>
          </label>
        </form>

        <div className="cmp-results">
          <div className="cmp-today" aria-live="polite">
            <span>Aujourd’hui, par année</span>
            <strong>{money(today)}</strong>
            <small>{detail.length ? detail.join(' · ') : 'Aucun coût direct : l’administration repose sur les bénévoles.'}</small>
          </div>
          <div className="cmp-plans" aria-live="polite">
            {PLANS.map((p) => {
              const monthly = Math.max(lots * p.rate, p.min);
              const setup = p.waivable && twoYear ? 0 : p.setup;
              const kept = replaced ? 0 : otherYear;
              const steady = monthly * 12 + kept;
              const year1 = monthly * (12 - (founder ? 3 : 0)) + setup + kept;
              const d = today - steady;
              return (
                <article key={p.key} className={`cmp-plan${p.key === fit ? ' cmp-fit' : ''}`}>
                  {p.key === fit && <span className="badge">Le plus proche de leur situation</span>}
                  <h3>{p.name}</h3>
                  <p className="muted small">{p.who}</p>
                  <div>
                    <div className="cmp-price">{money(monthly)}</div>
                    <div className="muted small">
                      par mois ({p.rate} $ × {lots} lots{lots * p.rate < p.min ? `, minimum ${p.min} $` : ''})
                    </div>
                  </div>
                  <dl className="cmp-rows">
                    <div><dt>Par année{kept ? ' (avec autres coûts)' : ''}</dt><dd>{money(steady)}</dd></div>
                    <div><dt>Mise en service</dt><dd>{setup ? money(setup) : 'annulée'}</dd></div>
                    <div><dt>Première année{founder ? ' (3 mois offerts)' : ''}</dt><dd>{money(year1)}</dd></div>
                  </dl>
                  {today > 0 ? (
                    <p className={`cmp-delta ${d >= 0 ? 'cmp-save' : 'cmp-more'}`}>{d >= 0 ? `Économie de ${money(d)} par année` : `${money(-d)} de plus par année`}</p>
                  ) : (
                    <p className="cmp-delta">À comparer avec le temps des bénévoles, mesuré pendant l’essai</p>
                  )}
                  <ul className="small">{p.incl.map((i) => <li key={i}>{i}</li>)}</ul>
                </article>
              );
            })}
          </div>
          <p className="cmp-hours">
            {h ? (
              <>
                <strong>{h} heures de bénévolat par mois</strong>, environ {(h * 12).toLocaleString('fr-CA')} heures par année. Pendant l’essai, on mesure combien Syndiqia en prend en charge : c’est un des critères de succès.
              </>
            ) : (
              <><strong>Temps des bénévoles :</strong> à estimer avec le conseil, puis à mesurer pendant l’essai.</>
            )}
          </p>
          <div className="muted small cmp-notes">
            <p>Prix par lot par mois, avant taxes : Autonome 6 $ (minimum 49 $), Assistée 17 $ (minimum 350 $), Complète 25 $ (minimum 500 $). Mise en service : Autonome 0 $ en libre-service ou 490 $ avec import assisté; Assistée 990 $ et Complète 1 490 $, annulés sur un contrat de 2 ans.</p>
            <p>Les associations et les syndicats ne récupèrent généralement pas la TPS et la TVQ : comparer les montants avant taxes des deux côtés. Aucuns frais de résiliation; prix garanti 24 mois (3 ans pour les clients fondateurs).</p>
            <p>Services exceptionnels publiés : gestion de travaux majeurs 5 % du contrat, assemblée supplémentaire en personne 350 $, suivi de réclamation d’assurance 95 $/h. Lettres de rappel et cotisation spéciale incluses.</p>
          </div>
        </div>
      </div>
      <AppFooter />
    </div>
  );
}
